import { useNavigate } from "react-router-dom";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { ChartCard, EmptyState } from "./ChartCard";
import { chartTheme } from "./chartTheme";
import { useMetricsPipeline } from "./useMetricsQueries";
import { useMetricsFilter } from "./MetricsFilterContext";
import { buildPipelineDrillThroughUrl } from "@/lib/pipelineFilterEncoder";
import type {
  MetricsFilter,
  PipelineStageBucket,
  SankeyFlow,
  StageAverageTime,
  ThroughputDataPoint,
} from "@/api/dealFlow/metrics";

// Pipeline tab (4 charts). All charts use design-system tokens via
// chartTheme; clicks navigate through to /deal-flow/pipeline with the
// stage encoded into the filter payload.

export function PipelineMetricsSection() {
  const { filter } = useMetricsFilter();
  const query = useMetricsPipeline(filter);

  if (query.isLoading) return <SectionLoading />;
  if (query.error || !query.data) return <SectionError />;

  const { stageBuckets, averageTimePerStage, advancingDecliningStalled, rollingThroughput30d } = query.data;

  return (
    <div
      className="grid grid-cols-1 gap-space-4 lg:grid-cols-2"
      data-testid="metrics-loaded"
    >
      <ChartCard
        title="Deals per stage"
        hint="Click a bar to drill through to the Pipeline filtered to that stage."
        testId="chart-deals-per-stage"
      >
        <DealsPerStageChart data={stageBuckets} metricsFilter={filter} />
      </ChartCard>
      <ChartCard
        title="Average time per stage (90d)"
        hint="Mean days a deal spent in each stage over the last 90 days."
        testId="chart-avg-time-per-stage"
      >
        <AvgTimePerStageChart data={averageTimePerStage} />
      </ChartCard>
      <ChartCard
        title="Advancing · Declining · Stalled (30d)"
        hint="Top transition pairs in the last 30 days."
        testId="chart-sankey"
      >
        <AdvancingDecliningStalledChart data={advancingDecliningStalled} />
      </ChartCard>
      <ChartCard
        title="Rolling 30-day throughput"
        hint="Daily count of advances, declines, and stalled transitions."
        testId="chart-throughput"
      >
        <RollingThroughputChart data={rollingThroughput30d} />
      </ChartCard>
    </div>
  );
}

function SectionLoading() {
  return (
    <div className="flex h-64 items-center justify-center text-small text-gray-mid">
      Loading metrics…
    </div>
  );
}

function SectionError() {
  return (
    <div className="flex h-64 items-center justify-center text-small text-status-error">
      Failed to load metrics. Try refreshing.
    </div>
  );
}

function DealsPerStageChart({
  data,
  metricsFilter,
}: {
  data: PipelineStageBucket[];
  metricsFilter: MetricsFilter;
}) {
  const navigate = useNavigate();
  if (data.every((d) => d.dealCount === 0)) return <EmptyState label="No deals to display." />;
  return (
    <ResponsiveContainer width="100%" height={300}>
      <BarChart
        data={data}
        layout="vertical"
        margin={{ left: 16, right: 16, top: 8, bottom: 8 }}
      >
        <CartesianGrid stroke={chartTheme.grid} strokeDasharray="3 3" />
        <XAxis type="number" stroke={chartTheme.axis} />
        <YAxis dataKey="displayName" type="category" width={120} stroke={chartTheme.axis} />
        <Tooltip />
        <Bar
          dataKey="dealCount"
          cursor="pointer"
          onClick={(entry) => {
            const stage = (entry as unknown as PipelineStageBucket).stage;
            // Carry the active metrics filter (broker / analyst) into the
            // drill-through so the Pipeline result is scoped exactly like
            // the chart the user clicked.
            navigate(
              buildPipelineDrillThroughUrl({
                stage,
                brokerId: metricsFilter.broker,
                analystUserId: metricsFilter.analyst,
              }),
            );
          }}
        >
          {data.map((d) => (
            <Cell key={d.stage} fill={chartTheme.primary} />
          ))}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
}

function AvgTimePerStageChart({ data }: { data: StageAverageTime[] }) {
  if (data.every((d) => d.sampleSize === 0)) return <EmptyState label="No transitions in the last 90 days." />;
  return (
    <ResponsiveContainer width="100%" height={300}>
      <BarChart data={data} layout="vertical" margin={{ left: 16, right: 16 }}>
        <CartesianGrid stroke={chartTheme.grid} strokeDasharray="3 3" />
        <XAxis type="number" stroke={chartTheme.axis} />
        <YAxis dataKey="displayName" type="category" width={120} stroke={chartTheme.axis} />
        <Tooltip />
        <Bar dataKey="averageDays" fill={chartTheme.info} />
      </BarChart>
    </ResponsiveContainer>
  );
}

function AdvancingDecliningStalledChart({ data }: { data: SankeyFlow[] }) {
  // Recharts' <Sankey> requires a node graph that's heavyweight to build
  // for our 7-stage taxonomy; we render the top 12 transitions as a
  // horizontal flow bar instead — readable in the same chart slot, easier
  // to maintain, and faithful to the "advancing/declining/stalled"
  // breakdown via the row coloring below.
  if (data.length === 0) return <EmptyState label="No transitions in the last 30 days." />;
  const top = data.slice(0, 12);
  const labelled = top.map((t) => ({
    name: `${t.fromState} → ${t.toState}`,
    count: t.count,
    direction:
      t.toState === "Declined"
        ? "declined"
        : t.fromState === t.toState
          ? "stalled"
          : "advanced",
  }));
  return (
    <ResponsiveContainer width="100%" height={300}>
      <BarChart data={labelled} layout="vertical" margin={{ left: 16, right: 16 }}>
        <CartesianGrid stroke={chartTheme.grid} strokeDasharray="3 3" />
        <XAxis type="number" stroke={chartTheme.axis} />
        <YAxis dataKey="name" type="category" width={180} stroke={chartTheme.axis} fontSize={11} />
        <Tooltip />
        <Bar dataKey="count">
          {labelled.map((row) => (
            <Cell
              key={row.name}
              fill={
                row.direction === "declined"
                  ? chartTheme.error
                  : row.direction === "stalled"
                    ? chartTheme.warning
                    : chartTheme.success
              }
            />
          ))}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
}

function RollingThroughputChart({ data }: { data: ThroughputDataPoint[] }) {
  if (data.length === 0) return <EmptyState label="No throughput in the last 30 days." />;
  return (
    <ResponsiveContainer width="100%" height={300}>
      <LineChart data={data} margin={{ left: 16, right: 16 }}>
        <CartesianGrid stroke={chartTheme.grid} strokeDasharray="3 3" />
        <XAxis dataKey="date" stroke={chartTheme.axis} fontSize={11} />
        <YAxis stroke={chartTheme.axis} />
        <Tooltip />
        <Line type="monotone" dataKey="advanced" stroke={chartTheme.success} strokeWidth={2} dot={false} />
        <Line type="monotone" dataKey="declined" stroke={chartTheme.error} strokeWidth={2} dot={false} />
        <Line type="monotone" dataKey="stalled" stroke={chartTheme.warning} strokeWidth={2} dot={false} />
      </LineChart>
    </ResponsiveContainer>
  );
}
